import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Platform} from '@angular/cdk/platform';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {FuseConfigService} from '@fuse/services/config.service';
import {FuseNavigationService} from '@fuse/components/navigation/navigation.service';
import {FuseSidebarService} from '@fuse/components/sidebar/sidebar.service';
import {FuseSplashScreenService} from '@fuse/services/splash-screen.service';
import {FuseTranslationLoaderService} from '@fuse/services/translation-loader.service';

import {navigation} from 'app/navigation/navigation';
import {locale as navigationEnglish} from 'app/navigation/i18n/en';
import {locale as navigationTurkish} from 'app/navigation/i18n/bn';
import {AuthService} from './services/auth.service';
import {User} from './models/user';
import {Title} from '@angular/platform-browser';
import {getEnumSelector, getOrganizationLogoName, getOrganizationTheme, urlConfig} from './services/utilities';
import {BaseService} from './services/base.service';
import {environment} from '../environments/environment';
import {APP_VERSION} from './config/version';
import {Router} from '@angular/router';
import {Resource} from './models/resource';
import {ApprovalRuleEnum} from './enums/approval-rule-enum';
import { AppConfigService } from './services/app-config.service';


@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

    fuseConfig: any;
    navigation: any;
    loggedUser: User;
    isOrgAdmin = false;
    isSuperAdmin = false;
    viewOnlyReports = false;
    resourceList: Resource[] = [];

    private _unsubscribeAll: Subject<any>;

    constructor(@Inject(DOCUMENT) private document: any,
                private _fuseConfigService: FuseConfigService,
                private _fuseNavigationService: FuseNavigationService,
                private _fuseSidebarService: FuseSidebarService,
                private _fuseSplashScreenService: FuseSplashScreenService,
                private _fuseTranslationLoaderService: FuseTranslationLoaderService,
                private _translateService: TranslateService,
                private _platform: Platform,
                private _titleService: Title,
                private service: BaseService,
                private auth: AuthService,
                private router: Router,
                private configService: AppConfigService) {
        this.loggedUser = this.auth.user;


        // <editor-fold desc="Process left sidebar navigation" defaultstate="collapsed">
        let userNavigation = [];
        const tempNavReportModule = [];
        if (this.loggedUser) {
            this.isOrgAdmin = this.auth.isOrgAdmin;
            this.isSuperAdmin = this.auth.isSuperAdmin;

            this.setTitle(this.loggedUser.Organization.OrganizationName);
            document.getElementById('favicon').href = '/assets/images/logos/' + getOrganizationLogoName(this.loggedUser.Organization.OrganizationCode);
            this._fuseConfigService.setConfig(getOrganizationTheme(this.loggedUser.Organization.OrganizationCode));
            this.viewOnlyReports = this.auth.hasAccess('OTHERS', 'VIEW_ONLY_REPORTS_FROM_WEB');
            if (this.loggedUser.UserRole.ModuleList.length > 0) {
                this.resourceList = this.loggedUser.UserRole.ModuleList.map(pModule => pModule.ResourceList).reduce((acc, num) => acc.concat(num), []);
            }

            if (this.isSuperAdmin) {
                navigation.forEach(pNavItem => {
                    if (pNavItem.id === 'superadmin') {
                        if (this.loggedUser.Organization.OrganizationCode === 'BZM') {
                            pNavItem.isActive = true;
                            userNavigation.push(...pNavItem.children);
                        }
                    }
                });
            }
            if (!this.isSuperAdmin) {
                navigation.forEach(pNavItem => {
                    if (pNavItem.id === 'report') {
                        const navReportModule = [];
                        if (pNavItem.children && pNavItem.children.length > 0) {
                            pNavItem.children.forEach(pNavItemReportModule => {
                                pNavItemReportModule.isActive = this._checkNavAccess(pNavItemReportModule.id.replace(/_report/g, ''), this.resourceList, false);
                                if (pNavItemReportModule.isActive) {
                                    const navReport = [];
                                    if (pNavItemReportModule.children && pNavItemReportModule.children.length > 0) {
                                        pNavItemReportModule.children.forEach(pNavItemReportModuleChild => {
                                            if (pNavItemReportModuleChild.children && pNavItemReportModuleChild.children.length > 0) {
                                                const navReportChild = [];
                                                pNavItemReportModuleChild.children.forEach(pSubMenu => {
                                                    pSubMenu.isActive = this._checkNavAccess(pSubMenu.id, this.resourceList, true);
                                                    if (pSubMenu.isActive) {
                                                        navReportChild.push(pSubMenu);
                                                    }
                                                });
                                                pNavItemReportModuleChild.children = navReportChild;
                                                if (pNavItemReportModuleChild.children.length > 0) {
                                                    navReport.push(pNavItemReportModuleChild);
                                                }
                                            } else {
                                                pNavItemReportModuleChild.isActive = this._checkNavAccess(pNavItemReportModuleChild.id, this.resourceList, true);
                                                if (pNavItemReportModuleChild.isActive) {
                                                    navReport.push(pNavItemReportModuleChild);
                                                }
                                            }
                                        });


                                    }
                                    if (navReport.length > 0) {
                                        pNavItemReportModule.children = navReport;
                                        navReportModule.push(pNavItemReportModule);
                                    }
                                }
                            });
                        }
                        navReportModule.sort((one, two) =>
                            (one.title > two.title ? 1 : -1));
                        tempNavReportModule.push(navReportModule);

                        if (navReportModule.length > 0) {
                            pNavItem.isActive = true;
                            pNavItem.children = navReportModule;
                            userNavigation.push(pNavItem);
                        }

                    } else {
                        if (this.viewOnlyReports && !this.isOrgAdmin) {
                            pNavItem.isActive = this._checkNavAccess(pNavItem.id, this.resourceList, false);
                            const navChildList = [];
                            if (pNavItem.id == 'ENGAGEMENT') {
                                if (pNavItem.children && pNavItem.children.length > 0) {
                                    pNavItem.children.forEach(pNavChildren => {
                                        pNavChildren.isActive = this._checkNavAccess(pNavChildren.id, this.resourceList, true);
                                        if (pNavChildren.isActive) {
                                            navChildList.push(pNavChildren);
                                        }
                                    });
                                    pNavItem.children = navChildList;
                                    if (pNavItem.children.length > 0) {
                                        userNavigation.push(pNavItem);
                                    }

                                }
                            }
                            return;
                        }
                        if (['DISTRIBUTOR_USER', 'CUSTOMER_USER'].includes(this.loggedUser.UserType) && pNavItem.id === 'user') {
                            return;
                        }

                        pNavItem.isActive = this._checkNavAccess(pNavItem.id, this.resourceList, false);
                        const navChild = [];


                        if (pNavItem.isActive && pNavItem.children && pNavItem.children.length > 0) {

                            pNavItem.children.forEach(pNavItemChild => {
                                if (pNavItemChild.children && pNavItemChild.children.length > 0) {
                                    const filteredChildren = [];
                                    pNavItemChild.children.forEach(pSubMenu => {
                                        pSubMenu.isActive = this._checkNavAccess(pSubMenu.id, this.resourceList, true);
                                        if (pSubMenu.isActive) {
                                            filteredChildren.push(pSubMenu);
                                            if (pSubMenu.title == 'Market Sales') {
                                                if (pSubMenu.url.includes('aggregate')) {
                                                    const canViewMarketPrimaryAggregateSales = this.auth.hasAccess('TARGET_ACHIEVEMENT', 'V_3_VIEW_MARKET_PRIMARY_SALES_TARGET_AGGREGATE_MARKET_PRODUCT_LINE_TARGET');
                                                    const canViewMarketSecondaryAggregateSales = this.auth.hasAccess('TARGET_ACHIEVEMENT', 'V_3_VIEW_MARKET_SECONDARY_SALES_TARGET_AGGREGATE_MARKET_PRODUCT_LINE_TARGET');

                                                    const canViewMarketPrimarySales = this.auth.hasAccess('TARGET_ACHIEVEMENT', 'V_3_VIEW_MARKET_PRIMARY_SALES_TARGET');
                                                    const canViewMarketSecondarySales = this.auth.hasAccess('TARGET_ACHIEVEMENT', 'V_3_VIEW_MARKET_SECONDARY_SALES_TARGET');

                                                    if ((canViewMarketPrimaryAggregateSales || canViewMarketSecondaryAggregateSales) && (canViewMarketPrimarySales || canViewMarketSecondarySales)) {
                                                        pSubMenu.title = 'Market Sales(Aggregate)';
                                                    }
                                                }

                                            }
                                        }

                                    });
                                    pNavItemChild.children = filteredChildren;
                                    if (pNavItemChild.children.length > 0) {
                                        navChild.push(pNavItemChild);
                                    }

                                } else {
                                    pNavItemChild.isActive = this._checkNavAccess(pNavItemChild.id, this.resourceList, true);
                                    if (pNavItemChild.isActive) {
                                        navChild.push(pNavItemChild);
                                    }
                                }
                            });
                        }

                        switch (pNavItem.id) {
                            case 'market':
                                if (this.loggedUser.MarketLevelHierarchy && this.loggedUser.MarketLevelHierarchy.length > 0) {
                                    this.loggedUser.MarketLevelHierarchy.forEach(pMarketLevel => {
                                        if (pMarketLevel != null && pMarketLevel.MarketLevelName != null) {
                                            navChild.push({
                                                id: pMarketLevel.MarketLevelName.toLowerCase(),
                                                title: pMarketLevel.MarketLevelName,
                                                type: 'item',
                                                url: '/market/list/' + pMarketLevel.Id,
                                                isActive: true
                                            });
                                        }
                                    });
                                    if (this.auth.hasAccess('MARKET', 'ROUTE_INFO_REPORT')) {
                                        navChild.push({
                                            id: 'market',
                                            title: 'Route Info',
                                            type: 'item',
                                            url: '/market/route-info',
                                            isActive: true
                                        });
                                    }
                                }
                                break;

                            case 'ORGANIZATION':
                                if (this.auth.hasAccess('ORGANIZATION', 'VIEW_ORGANIZATION')) {
                                    navChild.push({
                                        id: 'VIEW_ORGANIZATION',
                                        title: 'Settings',
                                        type: 'item',
                                        url: '/admin-settings/' + this.loggedUser.Organization.Id,
                                        isActive: true
                                    });
                                }
                                break;

                            case 'location':
                                if (pNavItem.title == 'Location') {
                                    if (this.auth.hasAccess('LOCATION', 'VIEW_DISTRIBUTOR_LOCATION')
                                        && this.auth.hasAccess('LOCATION', 'VIEW_CUSTOMER_LOCATION')) {
                                        navChild.push({
                                            id: 'VIEW_DISTRIBUTOR_LOCATION',
                                            title: 'Distributor/ Retailer Location',
                                            type: 'item',
                                            url: '/location/distributor-customer-location-list',
                                            isActive: true
                                        });
                                    } else if (this.auth.hasAccess('LOCATION', 'VIEW_DISTRIBUTOR_LOCATION')) {
                                        navChild.push({
                                            id: 'VIEW_DISTRIBUTOR_LOCATION',
                                            title: 'Distributor Location',
                                            type: 'item',
                                            url: '/location/distributor-location-list',
                                            isActive: true
                                        });
                                    } else if (this.auth.hasAccess('LOCATION', 'VIEW_CUSTOMER_LOCATION')) {
                                        navChild.push({
                                            id: 'VIEW_CUSTOMER_LOCATION',
                                            title: 'Retailer Location',
                                            type: 'item',
                                            url: '/location/customer-location-list',
                                            isActive: true
                                        });
                                    }
                                }
                                break;

                            case 'distributor':
                                if (this.auth.user.UserType !== 'CUSTOMER_USER') {
                                    if (this.auth.hasAccess('PAYMENT', 'VIEW_DISTRIBUTOR_BANK_ACCOUNT')) {
                                        navChild.push({
                                            id: 'view_distributor_bank_account',
                                            title: 'Bank Account List',
                                            type: 'item',
                                            url: '/distributor-bank-account/list',
                                            isActive: true
                                        });
                                    }
                                    if (this.auth.hasAccess('STOCK', 'VIEW_DISTRIBUTOR_STOCK')) {
                                        navChild.push({
                                            id: 'view_distributor_stock',
                                            title: 'Distributor Stock',
                                            type: 'item',
                                            url: '/distributor-stock/list',
                                            isActive: true
                                        });

                                        navChild.push({
                                            id: 'view_distributor_stock_transaction',
                                            title: 'Distributor Stock Transaction List',
                                            type: 'item',
                                            url: '/distributor-stock/transaction-list',
                                            isActive: true
                                        });
                                    }
                                    if (this.auth.hasAccess('STOCK', 'VIEW_RETURN_STOCK')) {
                                        navChild.push({
                                            id: 'view_distributor_return_stock',
                                            title: 'Return Stock',
                                            type: 'item',
                                            url: '/distributor-return-stock/list',
                                            isActive: true
                                        });

                                        navChild.push({
                                            id: 'view_distributor_return_stock_transaction',
                                            title: 'Return Stock Transaction List',
                                            type: 'item',
                                            url: '/distributor-return-stock/transaction-list',
                                            isActive: true
                                        });
                                    }
                                }
                                break;

                            case 'user':
                                if (this.auth.user.UserType !== 'CUSTOMER_USER') {
                                    if (this.auth.hasAccess('USER', 'VIEW_DISTRIBUTOR_USER')) {
                                        navChild.push({
                                            id: 'distributor_view_user',
                                            title: 'Distributor User List',
                                            type: 'item',
                                            url: '/distributor-user/list',
                                            isActive: true
                                        });
                                    }
                                    if (this.auth.hasAccess('USER', 'VIEW_DSR_USER')) {
                                        navChild.push({
                                            id: 'VIEW_DSR_USER',
                                            title: 'DSR User List',
                                            type: 'item',
                                            url: '/dsr-user/list',
                                            isActive: true
                                        });
                                    }
                                }
                                break;

                            case 'DEALER':
                                if (this.auth.hasAccess('STOCK', 'VIEW_DEALER_STOCK')) {
                                    navChild.push({
                                        id: 'VIEW_DEALER_STOCK',
                                        title: 'Dealer Stock Transaction List',
                                        type: 'item',
                                        url: '/dealer-stock/transaction-list',
                                        isActive: true
                                    });

                                    navChild.push({
                                        id: 'VIEW_DEALER_STOCK',
                                        title: 'Dealer Stock List',
                                        type: 'item',
                                        url: '/dealer-stock/stock-list',
                                        isActive: true
                                    });
                                }
                                break;

                            case 'POI':
                                if (this.auth.hasAccess('POI', 'CREATE_POI_TYPE') &&
                                    this.auth.hasAccess('POI', 'EDIT_POI_TYPE') && this.service.poiTypes.length > 0) {
                                    this.service.poiTypes.forEach(pPoiType => {
                                        navChild.push({
                                            id: 'VIEW_POI_TYPE',
                                            title: pPoiType.Name,
                                            type: 'item',
                                            url: '/poi/poi-type-list/' + pPoiType.Id,
                                            isActive: true
                                        });
                                    });
                                }
                                break;

                            case 'USER_ATTENDANCE':
                                if (this.auth.hasAccess('ADMIN', 'ADMIN_ACCESS')) {
                                    navChild.push({
                                        id: 'USER_ATTENDANCE',
                                        title: 'Attendance Submission Rule',
                                        type: 'item',
                                        url: '/attendance/attendance-submission-rule',
                                        isActive: false
                                    });

                                    navChild.push({
                                        id: 'USER_ATTENDANCE',
                                        title: 'Attendance Location Rule List',
                                        type: 'item',
                                        url: '/attendance/attendance-location-rule-list',
                                        isActive: false
                                    });
                                }
                                break;

                            case 'customer':
                                if (this.auth.user.UserType !== 'CUSTOMER_USER') {
                                    if (this.auth.hasAccess('PAYMENT', 'VIEW_CUSTOMER_PAYMENT')) {
                                        navChild.push({
                                            id: 'view_customer_payment',
                                            title: 'Payment List',
                                            type: 'item',
                                            url: '/customer-payment/list',
                                            isActive: true
                                        });
                                    }

                                    if (this.auth.hasAccess('STOCK', 'VIEW_RETAILER_STOCK')) {
                                        navChild.push({
                                            id: 'VIEW_RETAILER_STOCK',
                                            title: 'Retailer Stock',
                                            type: 'item',
                                            url: '/retailer-stock/list',
                                            isActive: true
                                        });
                                    }
                                }

                                break;

                            case 'order':
                                if (this.auth.hasAccess('ORDER', 'VIEW_SECONDARY_ORDER') && pNavItem.title != 'Transport') {
                                    if (this.loggedUser.Organization.OrganizationCode == 'akijp') {
                                        urlConfig.orderAddUrl = '/customer-order/add-for-akij-plastics';
                                        urlConfig.orderDetailsUrl = '/akij-plastics-customer-order-details';
                                        urlConfig.orderListUrl = '/customer-order/list-for-akij-plastics';
                                        urlConfig.orderEditUrl = 'edit-for-akij-plastics';
                                        navChild.push({
                                            id: 'view_secondary_order',
                                            title: 'Retailer Order List',
                                            type: 'item',
                                            url: '/customer-order/list-for-akij-plastics',
                                            isActive: false
                                        });
                                    } else if (this.loggedUser.Organization.OrganizationCode == 'afm') {
                                        urlConfig.orderAddUrl = '/customer-order/add-for-akij-flour';
                                        urlConfig.orderDetailsUrl = '/akij-flour-customer-order-details';
                                        urlConfig.orderListUrl = '/customer-order/list-for-akij-flour';
                                        urlConfig.orderEditUrl = 'edit-for-akij-flour';
                                        navChild.push({
                                            id: 'view_secondary_order',
                                            title: 'Retailer Order List',
                                            type: 'item',
                                            url: '/customer-order/list-for-akij-flour',
                                            isActive: false
                                        });
                                    } else if (this.loggedUser.Organization.OrganizationCode == 'rtm') {
                                        urlConfig.orderAddUrl = '/customer-order/add-for-rtm';
                                        urlConfig.orderDetailsUrl = '/rtm-customer-order-details';
                                        urlConfig.orderListUrl = '/customer-order/list-for-rtm';
                                        urlConfig.orderEditUrl = 'edit-for-rtm';
                                        navChild.push({
                                            id: 'view_secondary_order',
                                            title: 'Retailer Order List',
                                            type: 'item',
                                            url: '/customer-order/list-for-rtm',
                                            isActive: false
                                        });
                                    } else {
                                        navChild.push({
                                            id: 'view_secondary_order',
                                            title: 'Retailer Order List',
                                            type: 'item',
                                            url: '/customer-order/list',
                                            isActive: false
                                        });
                                    }
                                }
                                break;

                            case 'invoice':
                                if (this.auth.hasAccess('INVOICE', 'VIEW_CUSTOMER_RETAILER_INVOICE')) {
                                    if (this.loggedUser.Organization.OrganizationCode == 'rtm') {
                                        navChild.push({
                                            id: 'VIEW_CUSTOMER_RETAILER_INVOICE',
                                            title: 'Retailer Invoice List',
                                            type: 'item',
                                            url: '/customer-invoice/list-for-rtm',
                                            isActive: false
                                        });
                                    } else {
                                        navChild.push({
                                            id: 'VIEW_CUSTOMER_RETAILER_INVOICE',
                                            title: 'Retailer Invoice List',
                                            type: 'item',
                                            url: '/customer-invoice/list',
                                            isActive: false
                                        });
                                    }
                                }
                                break;

                            case 'APPROVAL_RULES':
                                const approvalRules = getEnumSelector(ApprovalRuleEnum);
                                if (this.auth.hasAccess('ADMIN', 'ADMIN_ACCESS')) {
                                    pNavItem.isActive = true;
                                    approvalRules.forEach(pItem => {
                                        navChild.push({
                                            id: 'APPROVAL_RULES_FOR_' + pItem.value,
                                            title: 'Approval Rules For ' + pItem.title,
                                            type: 'item',
                                            url: `/approval-rules/list-for-${pItem.title.toLowerCase()}`,
                                            isActive: false
                                        });
                                    });
                                }
                                break;
                        }
                        pNavItem.children = navChild;

                        if (pNavItem.isActive) {
                            if (navChild.length > 0) {
                                userNavigation.push(pNavItem);
                            } else if (pNavItem.id == 'dashboard' || pNavItem.id == 'POI') {
                                userNavigation.push(pNavItem);
                            }
                        }
                    }
                });
            }

            const [dashboard] = userNavigation.filter(pUserNavigation => pUserNavigation.title == 'Dashboard');
            const [report] = userNavigation.filter(pUserNavigation => pUserNavigation.title == 'Report');
            userNavigation = userNavigation.filter(pUserNavigation => (pUserNavigation.title != 'Dashboard' && pUserNavigation.title != 'Report'));

            userNavigation.sort((one, two) =>
                (one.title > two.title ? 1 : -1));

            userNavigation.unshift(dashboard);
            if (report) {
                userNavigation.push(report);
            }
        }
        this.navigation = userNavigation;
        // </editor-fold>

        // Register the navigation to the service
        this._fuseNavigationService.register('main', this.navigation);

        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation('main');

        // Add languages
        this._translateService.addLangs(['en', 'bn']);

        // Set the default language
        this._translateService.setDefaultLang('en');

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationEnglish, navigationTurkish);

        // Use a language
        this._translateService.use('en');

        // <editor-fold desc="ngxTranslate Fix" defaultstate="collapsed">
        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         *
         * Set the default language to 'en' and then back to 'tr'.
         * '.use' cannot be used here as ngxTranslate won't switch to a language that's already
         * been selected and there is no way to force it, so we overcome the issue by switching
         * the default language back and forth.
         **/
        /*setTimeout( () => {
         this._translateService.setDefaultLang( 'en' );
         this._translateService.setDefaultLang( 'tr' );
         } );*/
        // </editor-fold>

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    compareVersions(pVersion1: string, pVersion2: string): boolean {
        if (pVersion1 == null) {
            return true;
        }
        const browserLocalVersion = pVersion1.split('.').map(Number);
        const fileAppVersion = pVersion2.split('.').map(Number);
        const [browserYear, browserMonth, browserDate, browserVersion] = browserLocalVersion;
        const [fileYear, fileMonth, fileDate, fileVersion] = fileAppVersion;
        if (fileYear > browserYear) {
            return false;
        } else if (fileMonth > browserMonth) {
            return false;
        } else if (fileDate > browserDate) {
            return false;
        } else {
            return fileVersion > browserVersion;
        }
    }

    ngOnInit(): void {
        window.onload = () => {
            const appVersion = JSON.parse(localStorage.getItem('app_version') || null);
            if (appVersion) {
                if (this.compareVersions(appVersion, APP_VERSION)) {
                    this.auth.logout().subscribe(res => {
                        if (res) {
                            this.router.navigate(['auth/login']);
                            localStorage.setItem('app_version', JSON.stringify(APP_VERSION));
                        }
                    });
                }
            } else {
                localStorage.setItem('app_version', JSON.stringify(APP_VERSION));
            }

        };
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(pConfig => {
                this.fuseConfig = pConfig;

                // Boxed
                if (this.fuseConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                } else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for (let i = 0; i < this.document.body.classList.length; i++) {
                    const className = this.document.body.classList[i];

                    if (className.startsWith('theme-')) {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });
        this.loadGoogleMapsApi();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    loadGoogleMapsApi(): void {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.configService.mapApiKey}`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }

    toggleSidebarOpen(pKey): void {
        this._fuseSidebarService.getSidebar(pKey).toggleOpen();
    }

    setTitle(newTitle: string): void {
        this._titleService.setTitle(newTitle);
    }

    _checkNavAccess(pKey: string, pResources: Resource[], pIsChildren: boolean): boolean {
        let accessible = false;
        const keys = pKey.toUpperCase().split(',').map(key => key.trim());

        if (keys.includes('DASHBOARD')) {
            accessible = true;
        } else if (pResources && pResources.length > 0) {
            if (pIsChildren) {
                accessible = pResources.filter(pAuthority => pKey.toUpperCase() == pAuthority.ResourceEnum).length > 0;
            } else {
                const [parentModule] = this.loggedUser.UserRole.ModuleList.filter(pModule => keys.includes(pModule.ModuleNameEnum));
                if (parentModule) {
                    accessible = true;
                }
            }
        }
        return accessible;
    }
}
